import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import CategorySelection from "./CategorySelection";
import TimeSlotManager from "./TimeSlotManager";
import TimezoneSelector from "./TimezoneSelector";

interface SubscriptionFormProps {
  initialCategories: string[];
  initialTimeSlots: Array<{id?: string, time: string, categoryId: string | null}>;
  initialTimezone: string;
  isLoading: boolean;
  hasActiveSubscription: boolean;
  onSubmit: (data: {
    selectedCategories: string[];
    timeSlots: Array<{id?: string, time: string, categoryId: string | null}>;
    timezone: string;
  }) => void;
  onUnsubscribe: () => void;
}

const formatTimeWithDays = (timeSlot: { time: string; days?: { [key: string]: boolean } }) => {
  const activeDays = Object.entries(timeSlot.days || {})
    .filter(([_, active]) => active)
    .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1, 3))
    .join(", ");

  const formattedTime = new Date(`2000-01-01T${timeSlot.time}`).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });

  return `${formattedTime} (${activeDays || 'All days'})`;
};

export default function SubscriptionForm({
  initialCategories,
  initialTimeSlots,
  initialTimezone,
  isLoading,
  hasActiveSubscription,
  onSubmit,
  onUnsubscribe
}: SubscriptionFormProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories);
  const [timeSlots, setTimeSlots] = useState<Array<{id?: string, time: string, categoryId: string | null}>>(initialTimeSlots);
  const [timezone, setTimezone] = useState(initialTimezone);
  const [confirmUnsubscribe, setConfirmUnsubscribe] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedCategories.length === 0) {
      toast({
        title: "Categories required",
        description: "Please select at least one category of quotes.",
        variant: "destructive",
      });
      return;
    }
    
    if (timeSlots.length === 0) {
      toast({
        title: "Delivery times required",
        description: "Please add at least one delivery time.",
        variant: "destructive",
      });
      return;
    }

    const hasInvalidSlots = timeSlots.some(slot => !slot.categoryId && slot.categoryId !== 'random');
    if (hasInvalidSlots) {
      toast({
        title: "Complete all time slots",
        description: "Please select a category or 'Random Category' for each time slot.",
        variant: "destructive",
      });
      return;
    }

    onSubmit({ selectedCategories, timeSlots, timezone });

    toast({
      title: hasActiveSubscription ? "Subscription Updated" : "Subscription Created",
      description: (
        <div className="mt-2">
          <p>You will receive quotes at:</p>
          <ul className="list-disc pl-4 mt-1">
            {timeSlots.map((slot, index) => (
              <li key={index}>{formatTimeWithDays(slot)}</li>
            ))}
          </ul>
        </div>
      ),
    });
  };

  const handleUnsubscribe = () => {
    if (!confirmUnsubscribe) {
      toast({
        title: "Please confirm unsubscribe",
        description: "Check the box below to confirm you want to unsubscribe.",
        variant: "destructive",
      });
      return;
    }
    onUnsubscribe();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-[98%] mx-auto md:max-w-full">
      <CategorySelection 
        selectedCategories={selectedCategories} 
        setSelectedCategories={setSelectedCategories} 
      />
      
      <TimezoneSelector 
        timezone={timezone} 
        setTimezone={setTimezone} 
      />
      
      <TimeSlotManager 
        timeSlots={timeSlots} 
        setTimeSlots={setTimeSlots} 
        selectedCategories={selectedCategories}
      />
      
      <div className="flex flex-col gap-4 pt-4">
        <Button 
          type="submit" 
          className="w-full sm:w-auto"
          disabled={isLoading}
        >
          {isLoading ? 'Updating...' : hasActiveSubscription ? 'Update Subscription' : 'Create Subscription'}
        </Button>

        {hasActiveSubscription && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-start space-x-2 mb-3">
              <Checkbox 
                id="confirm-unsubscribe"
                checked={confirmUnsubscribe}
                onCheckedChange={(checked) => setConfirmUnsubscribe(checked as boolean)}
                className="mt-1"
              />
              <label
                htmlFor="confirm-unsubscribe"
                className="text-sm text-gray-600 leading-tight"
              >
                I understand that by unsubscribing, I will stop receiving all quote notifications.
              </label>
            </div>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleUnsubscribe}
              className="w-auto border-red-300 text-red-500 hover:bg-red-50 hover:text-red-600"
              disabled={isLoading || !confirmUnsubscribe}
            >
              Unsubscribe
            </Button>
          </div>
        )}
      </div>
    </form>
  );
}
